import { useState } from 'react';
import { ChevronDown, ChevronUp, Download, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import type { RepairLogEntry } from '../reconciliation-types';

interface RepairLogProps {
  entries: RepairLogEntry[];
}

export function RepairLog({ entries }: RepairLogProps) {
  const [expanded, setExpanded] = useState(true);

  if (entries.length === 0) return null;

  const exportCsv = () => {
    const headers = ['Czas', 'Akcja', 'Opis', 'Wynik', 'Zmiany'];
    const rows = entries.map(e => [
      e.timestamp.toLocaleString('pl-PL'),
      e.action,
      e.description,
      e.result,
      e.changes.map(c => `${c.field}: ${c.oldValue} → ${c.newValue}`).join('; '),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `repair-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resultIcon = (result: string) => {
    switch (result) {
      case 'success': return <CheckCircle className="w-3 h-3 text-success" />;
      case 'partial': return <AlertTriangle className="w-3 h-3 text-warning" />;
      default: return <XCircle className="w-3 h-3 text-danger" />;
    }
  };

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-raised/30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-text-secondary hover:text-text-primary"
      >
        <span className="flex items-center gap-2">
          Log operacji
          <span className="text-xs px-2 py-0.5 rounded-full bg-surface-hover/60 text-text-secondary">{entries.length}</span>
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          <div className="flex justify-end">
            <button
              onClick={exportCsv}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-accent-primary transition-colors"
            >
              <Download className="w-3 h-3" />
              Eksportuj CSV
            </button>
          </div>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {entries.map(entry => (
              <div key={entry.id} className="flex items-start gap-2 text-xs p-2 rounded-lg bg-surface-hover/50">
                {resultIcon(entry.result)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-text-secondary font-medium">{entry.description}</span>
                    <span className="text-text-muted text-[10px]">{entry.timestamp.toLocaleTimeString('pl-PL')}</span>
                  </div>
                  {entry.changes.length > 0 && (
                    <div className="mt-1 text-text-muted">
                      {entry.changes.slice(0, 3).map((c, i) => (
                        <span key={i}>{c.field}: {c.oldValue} → {c.newValue}{i < Math.min(entry.changes.length, 3) - 1 ? ', ' : ''}</span>
                      ))}
                      {entry.changes.length > 3 && <span> (+{entry.changes.length - 3})</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
